export interface FieldType {
    sort: (a: unknown, b: unknown) => number;
}

function isNil(value: unknown): boolean {
    return value === null || value === undefined;
}

function withNullsLast(
    compare: (a: unknown, b: unknown) => number,
): FieldType['sort'] {
    return (a, b) => {
        const aNil = isNil(a);
        const bNil = isNil(b);
        if (aNil && bNil) return 0;
        if (aNil) return 1;
        if (bNil) return -1;
        return compare(a, b);
    };
}

export const TEXT: FieldType = {
    sort: withNullsLast((a, b) => String(a).localeCompare(String(b))),
};

export const NUMBER: FieldType = {
    sort: withNullsLast((a, b) => Number(a) - Number(b)),
};

export const DATE: FieldType = {
    sort: withNullsLast(
        (a, b) =>
            new Date(a as string | number | Date).getTime() -
            new Date(b as string | number | Date).getTime(),
    ),
};

export const BOOL: FieldType = {
    sort: withNullsLast((a, b) => Number(a) - Number(b)),
};

export function ordinal(map: Record<string, number>): FieldType {
    const lookup = map as Record<string, number | undefined>;
    return {
        sort: withNullsLast((a, b) => {
            const av = lookup[String(a)];
            const bv = lookup[String(b)];
            const aUnmapped = av === undefined;
            const bUnmapped = bv === undefined;
            if (aUnmapped && bUnmapped) return 0;
            if (aUnmapped) return 1;
            if (bUnmapped) return -1;
            return av - bv;
        }),
    };
}

const CHUNK_PATTERN = /(?:\d+|\D+)/g;
const DIGITS_PATTERN = /^\d+$/;

function naturalCompare(a: string, b: string): number {
    const aChunks = a.match(CHUNK_PATTERN) ?? [];
    const bChunks = b.match(CHUNK_PATTERN) ?? [];
    const length = Math.max(aChunks.length, bChunks.length);
    for (let i = 0; i < length; i += 1) {
        const aChunk = aChunks[i] ?? '';
        const bChunk = bChunks[i] ?? '';
        const bothNumeric =
            DIGITS_PATTERN.test(aChunk) && DIGITS_PATTERN.test(bChunk);
        const diff = bothNumeric
            ? Number(aChunk) - Number(bChunk)
            : aChunk.localeCompare(bChunk);
        if (diff !== 0) return diff;
    }
    return 0;
}

export function natural(): FieldType {
    return {
        sort: withNullsLast((a, b) => naturalCompare(String(a), String(b))),
    };
}

export function autoCompare(a: unknown, b: unknown): number {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    if (a instanceof Date && b instanceof Date)
        return a.getTime() - b.getTime();
    return String(a).localeCompare(String(b));
}

export const AUTO: FieldType = { sort: withNullsLast(autoCompare) };
